package fr.frux.core.maps

import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.calculateEndPadding
import androidx.compose.foundation.layout.calculateStartPadding
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.LayoutDirection
import kotlin.math.PI
import kotlin.math.atan
import kotlin.math.ln
import kotlin.math.min
import kotlin.math.pow
import kotlin.math.sinh
import kotlin.math.tan

/**
 * Cote d'une tuile, en dp. Constante du Web Mercator telle que MapLibre l'emploie : a un zoom `z`,
 * le monde entier mesure `TILE_SIZE_DP * 2^z`.
 */
internal const val TILE_SIZE_DP = 512.0

/** Zoom maximal servi par les tuiles. Au-dela, cadrer ne montrerait plus que du vide. */
private const val MAX_FIT_ZOOM = 18f

/** Part de la vue qui reste utile meme si les marges demandees la depassent. */
private const val MIN_FREE_FRACTION = 0.25f

/**
 * Rectangle geographique englobant un ensemble de points.
 *
 * Ne traverse pas l'antimeridien : un lot a cheval sur +/-180 degres produirait un rectangle qui
 * fait le tour du globe. Sans consequence ici, ou les points cadres viennent tous d'une requete
 * de quelques kilometres de rayon.
 */
data class MapBounds(
    val southwest: Location,
    val northeast: Location,
) {
    val center: Location
        get() =
            Location(
                latitude = (southwest.latitude + northeast.latitude) / 2,
                longitude = (southwest.longitude + northeast.longitude) / 2,
            )

    /**
     * Diagonale du rectangle, en metres. Sert a reconnaitre un rectangle degenere (un seul point,
     * ou plusieurs confondus) : le cadrer demanderait au SDK un zoom infini.
     */
    val diagonalMeters: Double
        get() = southwest.distanceMetersTo(northeast)
}

/**
 * Position de camera qui fait tenir ce rectangle dans la zone libre d'une vue de [viewport],
 * c'est-a-dire hors des bords occupes par [padding].
 *
 * Calcule ici plutot que delegue a `fitCameraToBounds`/`animateCameraToBounds` du SDK : le cadrage
 * maison est testable sans emulateur, il rend la meme position sur les quatre cibles, et il repond
 * avant le premier rendu de la carte, ce qui permet de rejouer un cadrage en attente des que les
 * marges de l'ecran changent (cf. MapLibreFruxMap). Le prix : la pente de Mercator est reecrite ici.
 *
 * Le centre est pris au milieu *en projection*, pas au milieu des latitudes : sur Mercator les deux
 * different, et viser la moyenne des latitudes decale le cadre vers le pole.
 */
fun MapBounds.toCameraPosition(
    viewport: DpSize,
    padding: PaddingValues,
    layoutDirection: LayoutDirection,
): MapCameraPosition {
    val paddingHorizontal =
        padding.calculateStartPadding(layoutDirection) + padding.calculateEndPadding(layoutDirection)
    val paddingVertical = padding.calculateTopPadding() + padding.calculateBottomPadding()

    // Des surcouches genereuses sur un ecran court pourraient depasser la vue : garder une zone
    // utile non nulle, sinon aucun zoom n'est calculable.
    val availableWidth = (viewport.width - paddingHorizontal).coerceAtLeast(viewport.width * MIN_FREE_FRACTION)
    val availableHeight = (viewport.height - paddingVertical).coerceAtLeast(viewport.height * MIN_FREE_FRACTION)

    val southY = mercatorY(southwest.latitude)
    val northY = mercatorY(northeast.latitude)
    val spanLongitude = northeast.longitude - southwest.longitude
    val spanY = northY - southY

    // Un rectangle sans etendue (un point) ne contraint aucun zoom : on prend le maximum, a charge
    // de l'appelant de preferer un simple recentrage dans ce cas.
    val zoomForWidth =
        if (spanLongitude <= 0) {
            MAX_FIT_ZOOM.toDouble()
        } else {
            log2(availableWidth.value * 360.0 / (spanLongitude * TILE_SIZE_DP))
        }
    val zoomForHeight =
        if (spanY <= 0) {
            MAX_FIT_ZOOM.toDouble()
        } else {
            log2(availableHeight.value * 2 * PI / (spanY * TILE_SIZE_DP))
        }

    // Le plus contraignant des deux axes : c'est lui qui decide si tout tient a l'ecran.
    val zoom = min(zoomForWidth, zoomForHeight).coerceIn(0.0, MAX_FIT_ZOOM.toDouble())
    val worldDp = TILE_SIZE_DP * 2.0.pow(zoom)

    // Le rectangle doit etre centre dans la zone *libre*, pas dans la vue entiere : avec une barre
    // de recherche en haut et une nav en bas, viser le centre de la vue glisse les points du haut
    // sous les surcouches. La camera vise donc a cote, de la moitie de l'ecart des marges.
    val offsetX = (padding.calculateStartPadding(layoutDirection) - padding.calculateEndPadding(layoutDirection)) / 2
    val offsetY = (padding.calculateTopPadding() - padding.calculateBottomPadding()) / 2

    val center =
        Location(
            latitude = inverseMercatorY((southY + northY) / 2 + offsetY.value / worldDp * 2 * PI),
            longitude = (southwest.longitude + northeast.longitude) / 2 - offsetX.value / worldDp * 360.0,
        )

    return MapCameraPosition(target = center, zoom = zoom.toFloat())
}

/** Ordonnee Mercator, en radians (l'equateur vaut 0, les poles tendent vers +/-PI). */
internal fun mercatorY(latitude: Double): Double = ln(tan(PI / 4 + latitude * PI / 360))

internal fun inverseMercatorY(y: Double): Double = atan(sinh(y)) * 180 / PI

private fun log2(value: Double): Double = ln(value) / ln(2.0)

/** Rectangle englobant, ou null si la collection est vide — il n'y a alors rien a cadrer. */
fun Iterable<Location>.toMapBounds(): MapBounds? {
    val iterator = iterator()
    if (!iterator.hasNext()) return null

    val first = iterator.next()
    var south = first.latitude
    var north = first.latitude
    var west = first.longitude
    var east = first.longitude

    iterator.forEach { location ->
        south = minOf(south, location.latitude)
        north = maxOf(north, location.latitude)
        west = minOf(west, location.longitude)
        east = maxOf(east, location.longitude)
    }

    return MapBounds(
        southwest = Location(south, west),
        northeast = Location(north, east),
    )
}
